import { ActiveRegisterForm } from 'props';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { IExternalContacts, IUserRegister } from 'entities';
import { IAddressViewModel, IFileViewModel } from 'viewModels';
import { v4 as uuid } from 'uuid';
import { useQuery } from 'react-query';
import { normalizeExternalContactFromApi } from '../utils/validateExternalContact';
import {
  FileUsageType,
  RegistrationOrigin,
  TypeContact,
  TypeUser,
  UserProfileType,
  ValidationType,
} from '../types/enums';
import { CompanyController } from '../controller/CompanyController';
import { useAuth } from './auth';
import { UserController } from '../controller/UserController';
import { Download } from '../controller/FileController';
import useWebSocket from '../hooks/useWebSocket';
import { useApp } from './app';

type RegisterFormContextProps = {
  children: ReactNode;
};
interface IInitialValues {
  userType: TypeUser;
  setUserType: React.Dispatch<React.SetStateAction<TypeUser>>;
  firstName: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  lastName: string;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  document: string;
  setDocument: React.Dispatch<React.SetStateAction<string>>;
  isServiceProvider: boolean;
  setIsServiceProvider: React.Dispatch<React.SetStateAction<boolean>>;
  isServiceProviderCurrentSelection: boolean;
  setIsServiceProviderCurrentSelection: React.Dispatch<React.SetStateAction<boolean>>;
  inscriptionNumber: string;
  setInscriptionNumber: React.Dispatch<React.SetStateAction<string>>;
  ufInscriptionNumber: string | undefined;
  setUfInscriptionNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  typeProfile: UserProfileType | undefined;
  setTypeProfile: React.Dispatch<React.SetStateAction<UserProfileType | undefined>>;
  typeValidation: ValidationType | undefined;
  setTypeValidation: React.Dispatch<React.SetStateAction<ValidationType | undefined>>;
  verificationPictures: (IFileViewModel | undefined)[];
  setVerificationPictures: React.Dispatch<React.SetStateAction<(IFileViewModel | undefined)[]>>;
  isVerified: boolean;
  isLoading: boolean;
  setIsVerified: React.Dispatch<React.SetStateAction<boolean>>;
  presentationLetter: string;
  setPresentationLetter: React.Dispatch<React.SetStateAction<string>>;
  activeForm: ActiveRegisterForm;
  setActiveForm: React.Dispatch<React.SetStateAction<ActiveRegisterForm>>;
  tradingName: string;
  setTradingName: React.Dispatch<React.SetStateAction<string>>;
  address: IAddressViewModel;
  setAddress: React.Dispatch<React.SetStateAction<IAddressViewModel>>;
  formSubmited: boolean;
  setFormSubmited: React.Dispatch<React.SetStateAction<boolean>>;
  pictureProfile: IFileViewModel | undefined;
  setPictureProfile: React.Dispatch<React.SetStateAction<IFileViewModel | undefined>>;
  externalContacts: IExternalContacts[];
  setExternalContacts: React.Dispatch<React.SetStateAction<IExternalContacts[]>>;
  linkFacebook: string;
  setLinkFacebook: React.Dispatch<React.SetStateAction<string>>;
  linkInstagram: string;
  setLinkInstagram: React.Dispatch<React.SetStateAction<string>>;
  linkLinkedIn: string;
  setLinkLinkedIn: React.Dispatch<React.SetStateAction<string>>;
  linkTwitter: string;
  setLinkTwitter: React.Dispatch<React.SetStateAction<string>>;
  linkSite: string;
  setLinkSite: React.Dispatch<React.SetStateAction<string>>;
  url: string;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
  hasError: boolean;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  userData: IUserRegister;
  setUserData: React.Dispatch<React.SetStateAction<IUserRegister>>;
  handleUpdate: boolean;
  setHandleUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  registrationOrigin: RegistrationOrigin | undefined;
  setRegistrationOrigin: React.Dispatch<React.SetStateAction<RegistrationOrigin | undefined>>;
}
const RegisterFormContext = React.createContext({} as IInitialValues);
const RegisterFormContextProvider = ({ children }: RegisterFormContextProps) => {
  const { setLoading } = useApp();
  const { user, tokenIsValid, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [userType, setUserType] = useState<TypeUser>(TypeUser.BASEUSER);
  const [formSubmited, setFormSubmited] = useState(false);
  const [activeForm, setActiveForm] = useState<ActiveRegisterForm>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [document, setDocument] = useState<string>('');
  const [isServiceProvider, setIsServiceProvider] = useState(false);
  const [isServiceProviderCurrentSelection, setIsServiceProviderCurrentSelection] = useState(false);
  const [ufInscriptionNumber, setUfInscriptionNumber] = useState<string>();
  const [presentationLetter, setPresentationLetter] = useState<string>('');
  const [tradingName, setTradingName] = useState<string>('');
  const [address, setAddress] = useState<IAddressViewModel>({} as IAddressViewModel);
  const [pictureProfile, setPictureProfile] = useState<IFileViewModel | undefined>();
  const [linkFacebook, setLinkFacebook] = useState<string>('');
  const [linkInstagram, setLinkInstagram] = useState<string>('');
  const [linkTwitter, setLinkTwitter] = useState<string>('');
  const [linkLinkedIn, setLinkLinkedIn] = useState<string>('');
  const [typeProfile, setTypeProfile] = useState<UserProfileType | undefined>(undefined);
  const [inscriptionNumber, setInscriptionNumber] = useState<string>('');
  const [typeValidation, setTypeValidation] = useState<ValidationType | undefined>(undefined);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [linkSite, setLinkSite] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [hasError, setHasError] = useState(false);
  const [handleUpdate, setHandleUpdate] = useState(false);
  const [userData, setUserData] = useState<IUserRegister>({} as IUserRegister);
  const [verificationPictures, setVerificationPictures] = useState<(IFileViewModel | undefined)[]>([
    undefined,
    undefined,
  ]);
  const [externalContacts, setExternalContacts] = useState<IExternalContacts[]>([
    {
      id: uuid(),
      value: '',
      isVerified: false,
      isValid: false,
      isEmpty: true,
      type: TypeContact.EMAIL,
      alreadySentConfirmation: false,
      alreadyExists: false,
    },
  ]);
  const [registrationOrigin, setRegistrationOrigin] = useState<RegistrationOrigin | undefined>(
    undefined,
  );

  const { isLoading: isLoadingUserData, isFetching } = useQuery(
    'user',
    () =>
      user.type === TypeUser.USER
        ? UserController.findUserById(user.id)
        : [TypeUser.COMPANY, TypeUser.ADM].includes(user.type)
        ? CompanyController.findCompanyById(user.id)
        : UserController.getBaseUserIncompleteRegister(),
    {
      onSuccess: (data) => {
        if (!data) return;
        setUserType(data.type ?? TypeUser.BASEUSER);
        setUserData(data);
        setFirstName(data.firstName ?? '');
        setLastName(data.lastName ?? '');
        setEmail(data.email ?? '');
        setDocument(data.document?.trim() ?? '');
        setIsServiceProvider(!!data.isServiceProvider);
        setIsServiceProviderCurrentSelection(!!data.isServiceProvider);
        setPresentationLetter(data.presentation ?? '');
        setAddress(data.address ?? ({} as IAddressViewModel));
        setExternalContacts(
          data.externalContacts?.length
            ? data.externalContacts?.map((prev) =>
                normalizeExternalContactFromApi({
                  ...prev,
                  alreadyExists: true,
                  alreadySentConfirmation: true,
                }),
              )
            : [
                {
                  id: uuid(),
                  value: '',
                  isVerified: false,
                  isValid: false,
                  alreadySentConfirmation: false,
                  alreadyExists: false,
                  isEmpty: true,
                  type: TypeContact.EMAIL,
                },
              ],
        );
        setLinkFacebook(data.linkFacebook ?? '');
        setLinkInstagram(data.linkInstagram ?? '');
        setLinkLinkedIn(data.linkLinkedIn ?? '');
        setLinkTwitter(data.linkTwitter ?? '');
        setLinkSite(data.linkSite ?? '');
        setUrl(data.url ?? '');
        if (data.profilePictureId) {
          Download(data.profilePictureId).then((blob) => {
            const file = new File([blob], 'profilePicture');
            setPictureProfile({ file, id: data.profilePictureId, url: URL.createObjectURL(file) });
          });
        }
        if (data.type === TypeUser.USER) {
          setTypeProfile(data.profileType);
          setInscriptionNumber(data.inscriptionNumber ?? '');
          setTypeValidation(data.validationType);
          setIsVerified(!!data.isVerified);
          setUfInscriptionNumber(data.ufInscriptionNumber);
          const newVerificationPictures = [...verificationPictures];
          if (data.userValidationPictures) {
            const promises = data.userValidationPictures
              .map(async (item) => {
                if (item.id) {
                  return Download(item.validationPictureId).then((blob) => {
                    const documentFile = new File(
                      [blob],
                      item.validationPicture.name + item.validationPicture.extension,
                    );
                    if (item.type === FileUsageType.BACK) {
                      newVerificationPictures[1] = {
                        file: documentFile,
                        fileUsageType: item.type,
                        contentType: item.validationPicture.contentType,
                        extension: item.validationPicture.extension,
                        url: URL.createObjectURL(blob),
                      };
                    } else {
                      newVerificationPictures[0] = {
                        file: documentFile,
                        fileUsageType: item.type,
                        contentType: item.validationPicture.contentType,
                        extension: item.validationPicture.extension,
                        url: URL.createObjectURL(blob),
                      };
                    }
                  });
                }
                return undefined;
              })
              .filter((p) => p !== undefined);
            Promise.all(promises).then(() => setVerificationPictures(newVerificationPictures));
          }
        } else if (
          data.type !== undefined &&
          [TypeUser.COMPANY, TypeUser.ADM].includes(data.type)
        ) {
          setTradingName(data.tradingName ?? '');
        }
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: !!user.id && tokenIsValid && user.type !== undefined,
    },
  );

  useWebSocket<string>({
    url: `user/${user.id}/update-profile-picture`,
    onMessage: (profilePictureId) => {
      setUser((prev) => ({ ...prev, profilePictureId }));
      Download(profilePictureId).then((blob) => {
        const file = new File([blob], 'profilePicture');
        setPictureProfile({ file, id: profilePictureId, url: URL.createObjectURL(file) });
      });
    },
  });

  useEffect(() => {
    if (!tokenIsValid) {
      setUserType({} as TypeUser);
      setFirstName('');
      setLastName('');
      setEmail('');
      setDocument('');
      setIsServiceProvider(false);
      setInscriptionNumber('');
      setTypeProfile(undefined);
      setTypeValidation(undefined);
      setVerificationPictures([undefined, undefined]);
      setIsVerified(false);
      setPresentationLetter('');
      setActiveForm('');
      setTradingName('');
      setAddress({} as IAddressViewModel);
      setFormSubmited(false);
      setPictureProfile(undefined);
      setExternalContacts([
        {
          id: uuid(),
          value: '',
          isVerified: false,
          isValid: false,
          alreadySentConfirmation: false,
          alreadyExists: false,
          isEmpty: true,
          type: TypeContact.EMAIL,
        },
      ]);
      setLinkFacebook('');
      setLinkInstagram('');
      setLinkTwitter('');
      setLinkLinkedIn('');
      setLinkSite('');
      setUrl('');
      setHasError(false);
      setHandleUpdate(false);
      setUserData({} as IUserRegister);
      setUfInscriptionNumber(undefined);
      setIsServiceProviderCurrentSelection(false);
      setRegistrationOrigin(undefined);
    }
  }, [tokenIsValid]);

  useEffect(() => {
    setLoading(isLoadingUserData || isFetching);
    setIsLoading(isLoadingUserData || isFetching);
  }, [setLoading, isFetching, isLoadingUserData]);

  const contextValues = useMemo(
    () => ({
      activeForm,
      setActiveForm,
      userType,
      setUserType,
      firstName,
      setFirstName,
      lastName,
      setLastName,
      email,
      setEmail,
      document,
      setDocument,
      typeProfile,
      setTypeProfile,
      typeValidation,
      setTypeValidation,
      inscriptionNumber,
      setInscriptionNumber,
      isServiceProvider,
      setIsServiceProvider,
      isServiceProviderCurrentSelection,
      setIsServiceProviderCurrentSelection,
      isVerified,
      setIsVerified,
      presentationLetter,
      setPresentationLetter,
      tradingName,
      setTradingName,
      address,
      setAddress,
      formSubmited,
      setFormSubmited,
      pictureProfile,
      setPictureProfile,
      externalContacts,
      setExternalContacts,
      linkFacebook,
      setLinkFacebook,
      linkInstagram,
      setLinkInstagram,
      linkLinkedIn,
      setLinkLinkedIn,
      linkTwitter,
      setLinkTwitter,
      linkSite,
      setLinkSite,
      url,
      setUrl,
      setVerificationPictures,
      verificationPictures,
      hasError,
      setHasError,
      userData,
      isLoading,
      setUserData,
      handleUpdate,
      setHandleUpdate,
      ufInscriptionNumber,
      setUfInscriptionNumber,
      registrationOrigin,
      setRegistrationOrigin,
    }),
    [
      activeForm,
      userType,
      firstName,
      lastName,
      email,
      document,
      typeProfile,
      typeValidation,
      isLoading,
      inscriptionNumber,
      isServiceProvider,
      isServiceProviderCurrentSelection,
      isVerified,
      presentationLetter,
      tradingName,
      address,
      formSubmited,
      pictureProfile,
      externalContacts,
      linkFacebook,
      linkInstagram,
      linkLinkedIn,
      linkTwitter,
      linkSite,
      url,
      verificationPictures,
      hasError,
      userData,
      handleUpdate,
      ufInscriptionNumber,
      registrationOrigin,
    ],
  );

  return (
    <RegisterFormContext.Provider value={contextValues}>{children}</RegisterFormContext.Provider>
  );
};

export const useRegisterForm = () => useContext(RegisterFormContext);
export default RegisterFormContextProvider;
